import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import {
    ForgotPasswordRequest,
    LoginRequest,
    LoginResponse,
    RefreshTokenRequest,
    ResetPasswordRequest,
} from '@lib/interfaces/auth.interface';
import { clearAppSession, setAppSession } from '@lib/store';
import {
    selectIsAuthenticated,
    selectUserRoles,
    selectUserInitials,
    selectUserId,
    selectIsMisc,
} from '@lib/store/app-session.selectors';
import { Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { ToasterService } from '../../components/toaster/toaster.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly _router = inject(Router);
    private readonly _http = inject(HttpClient);
    private readonly _store = inject(Store);
    private readonly _toaster = inject(ToasterService);

    readonly isAuthenticated$ = this._store.select(selectIsAuthenticated);
    readonly currentUserRoles$ = this._store.select(selectUserRoles);
    readonly currentUserInitials$ = this._store.select(selectUserInitials);
    readonly currentUserId$ = this._store.select(selectUserId);

    constructor() {}

    get isAuthenticated(): boolean {
        let isAuth = false;
        this._store
            .select(selectIsAuthenticated)
            .subscribe((auth) => (isAuth = auth))
            .unsubscribe();
        return isAuth;
    }

    get currentUserRoles(): string[] {
        let roles: string[] = [];
        this._store
            .select(selectUserRoles)
            .subscribe((r) => (roles = r))
            .unsubscribe();
        return roles;
    }

    get currentUserInitials(): string {
        let initials = 'U';
        this._store
            .select(selectUserInitials)
            .subscribe((i) => (initials = i))
            .unsubscribe();
        return initials;
    }

    get currentUserId(): string {
        let userId = '';
        this._store
            .select(selectUserId)
            .subscribe((id) => (userId = id))
            .unsubscribe();
        return userId;
    }

    get isMiscLogin(): boolean {
        let isMisc = false;
        this._store
            .select(selectIsMisc)
            .subscribe((misc) => (isMisc = misc))
            .unsubscribe();
        return isMisc;
    }

    // Signin with Email
    logout(): void {
        this._store.dispatch(clearAppSession());
    }

    signinWithEmail(credentials: LoginRequest, returnUrl: string = '/faculty'): Observable<LoginResponse> {
        return this._http.post<LoginResponse>(`${environment.API_URL}/auth/login`, credentials).pipe(
            tap((response) => {
                this._store.dispatch(setAppSession({ session: { ...response.data } }));
                this._router.navigateByUrl(returnUrl);
            }),
        );
    }

    forgotPassword(request: ForgotPasswordRequest): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/forgot-password`, request);
    }

    resetPassword(request: ResetPasswordRequest): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/set-password`, request);
    }

    /**
     * Calls the refresh token endpoint and returns the response.
     * @param request The refresh token request object containing Authorization and refreshToken
     */
    refreshToken(request: RefreshTokenRequest): Observable<any> {
        const params = new HttpParams().set('refreshToken', request.refreshToken);
        return this._http.post<any>(
            `${environment.API_URL}/auth/refresh-token`,
            {},
            {
                headers: { Authorization: request.Authorization },
                params,
            },
        );
    }

    /**
     * Calls the logout endpoint and clears session on success.
     */
    logoutUser(): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/logout`, {}).pipe(
            tap({
                next: () => {
                    this.logout();
                },
                error: () => {
                    this._toaster.show({ message: 'Logout failed. Please try again.', status: 'error' });
                },
            }),
        );
    }
}
